// Shared helpers for the specialist families (Cantor, Lancer, Husk, Brood). Render-only.
import * as THREE from 'three/webgpu';
import { ArtBuilder, at, limb, plate } from './kit';

export type V3 = [number, number, number];

/** faceted crystal: an octahedron stretched to radius r and height h, placed at p with an optional tilt */
export function gem(r: number, h: number, p: V3, rx = 0, ry = 0, rz = 0): THREE.BufferGeometry {
  return at(new THREE.OctahedronGeometry(1, 0), p[0], p[1], p[2], rx, ry, rz, [r, h / 2, r]);
}

/** low-poly ball */
export function ball(r: number, p: V3, s: V3 = [1, 1, 1], w = 10, h = 7): THREE.BufferGeometry {
  return at(new THREE.SphereGeometry(r, w, h), p[0], p[1], p[2], 0, 0, 0, s);
}

/** a flat plate from a to b (centre between them), oriented along the segment, width w (x), thickness t */
export function slab(a: V3, b: V3, w: number, t: number, r = 0.015): THREE.BufferGeometry {
  const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b), d = B.clone().sub(A), len = d.length();
  const g = plate(w, t, len, Math.min(r, t / 2 - 0.001, w / 2 - 0.001), 1);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), d.normalize());
  g.applyQuaternion(q);
  const c = A.add(B).multiplyScalar(0.5);
  return g.translate(c.x, c.y, c.z);
}

export interface LegSpec {
  hip: V3; knee: V3; ankle: V3;
  rThigh: number; rShin: number;
  /** foot plate: width, height, length; toe offset forward of the ankle */
  foot: [number, number, number]; toe?: number;
  thigh: number; shin: number; kneeCol: number; footCol: number;
  swing: number; sphase: number; raise?: number;
  /** extra knee guard size (0 = small joint ball only) */
  guard?: number;
  /** limb segment count (default 6) */
  seg?: number;
  /** skip the knee joint ball */
  noKnee?: boolean;
}

/** A bent biped leg (thigh, knee, shin, foot). Everything rotates about the hip so the leg never detaches. */
export function leg(b: ArtBuilder, s: LegSpec): void {
  const o = { pivot: s.hip, swing: s.swing, sphase: s.sphase, raise: s.raise ?? 0 };
  b.add(limb(s.hip, s.knee, s.rThigh, s.rThigh * 0.85, s.seg ?? 6), { color: s.thigh, ...o });
  b.add(limb(s.knee, s.ankle, s.rShin, s.rShin * 0.75, s.seg ?? 6), { color: s.shin, ...o });
  if (!s.noKnee) b.add(ball(s.rShin * 1.15, s.knee, [1, 1, 1], 6, 4), { color: s.kneeCol, ...o });
  if (s.guard) b.add(at(plate(s.guard, s.guard * 1.2, s.guard * 0.5, 0.02, 1), s.knee[0], s.knee[1] + 0.02, s.knee[2] + s.rShin * 0.9, -0.15), { color: s.kneeCol, ...o });
  const [fw, fh, fl] = s.foot;
  b.add(at(plate(fw, fh, fl, Math.min(0.03, fh / 2 - 0.002), 1), s.ankle[0], fh / 2, s.ankle[2] + (s.toe ?? fl * 0.25)), { color: s.footCol, ...o });
}

/** Swing amplitude for a hip-pivoted leg so the planted foot moves with the ground (no skating):
 *  the foot sweeps 2·L·θ per half cycle while the body travels π/stride, so θ = π / (2·L·stride). */
export function noSkateSwing(hipH: number, stride: number): number { return Math.PI / (2 * hipH * stride); }
/** Body dip (negative bob) that keeps both feet on y=0 when the legs are at full swing (compass gait). */
export function compassDip(hipH: number, swing: number): number { return -hipH * (1 - Math.cos(swing)); }
